import {
    afterRenderEffect,
    ElementRef,
    signal,
    viewChild,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    inject,
    input,
    output,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { NgIcon } from '@ng-icons/core';
import { FEATURE_FLAG, FeatureFlagService } from '@quipex/core/config';
import type { AppTopBarBuildingIdentity } from '@quipex/core/layout';
import type { ResolvedProblemDetails } from '@quipex/core/utils';
import {
    BuildingIdentityFact,
    PROPERTY_TYPE_IDENTITY,
    QpxBuildingScenery,
    SUB_PREMISE_TYPE_IDENTITY,
    getDocumentStorageCapacityStatus,
} from '@quipex/feature/building-context';
import { BuildingStatus } from '@quipex/shared/contracts';
import type {
    BuildingDetailsResponse,
    GetBuildingDocumentStorageUsageResponse,
} from '@quipex/shared/contracts';
import {
    BuildingPropertyTypeIconPipe,
    BuildingSubPremiseTypeIconPipe,
    AddressPipe,
    BuildingNamePipe,
    ErrorState,
    QpxButton,
    QpxHeroIcon,
    QpxIcon,
    QpxSkeleton,
    QpxTypography,
    QpxTooltip,
} from '@quipex/shared/ui';
import { ActionState } from './action-state';
import { FocusedBuildingPreviewFact } from './focused-building-preview-fact/focused-building-preview-fact';
import { toFocusedBuildingViewModel } from './mapper';
import { PreviewStorageCapacityBanner } from './preview-storage-capacity-banner/preview-storage-capacity-banner';

@Component({
    selector: 'qpx-focused-building-preview',
    imports: [
        BuildingPropertyTypeIconPipe,
        BuildingSubPremiseTypeIconPipe,
        BuildingIdentityFact,
        AddressPipe,
        FocusedBuildingPreviewFact,
        BuildingNamePipe,
        PreviewStorageCapacityBanner,
        ErrorState,
        MatButtonModule,
        QpxTooltip,
        NgIcon,
        QpxButton,
        QpxBuildingScenery,
        QpxHeroIcon,
        QpxIcon,
        QpxSkeleton,
        QpxTypography,
    ],
    templateUrl: './focused-building-preview.html',
    styleUrl: './focused-building-preview.scss',
    providers: [ActionState],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        role: 'article',
        '[attr.aria-labelledby]': 'headingId',
    },
})
export class FocusedBuildingPreview {
    protected readonly binEnabled = inject(FeatureFlagService).flagEnabled(
        FEATURE_FLAG.BuildingIdentificationNumbers
    );
    protected readonly propertyTypeIdentity = PROPERTY_TYPE_IDENTITY;
    protected readonly subPremiseTypeIdentity = SUB_PREMISE_TYPE_IDENTITY;
    //#region Dependency injection
    private readonly actionState = inject(ActionState);
    private readonly destroyRef = inject(DestroyRef);
    //#endregion

    //#region Constants
    protected readonly headingId = 'focused-building-title';
    protected readonly loadingTiles = [0, 1, 2, 3, 4] as const;
    //#endregion

    //#region Inputs
    public readonly building = input.required<AppTopBarBuildingIdentity>();
    public readonly buildingDetails = input<BuildingDetailsResponse | null>(
        null
    );
    public readonly loading = input(false);
    public readonly loadError = input<ResolvedProblemDetails | null>(null);
    public readonly storageUsage =
        input<GetBuildingDocumentStorageUsageResponse>();
    public readonly onDeleted = input<((buildingId: string) => void) | null>(
        null
    );
    //#endregion

    //#region Outputs
    public readonly openRequested = output<void>();
    public readonly deleted = output<string>();
    public readonly retryRequested = output<void>();
    //#endregion

    //#region State
    protected readonly details = computed(() => {
        const details = this.buildingDetails();
        return details ? toFocusedBuildingViewModel(details) : null;
    });
    protected readonly displayBuilding = computed(() => {
        const details = this.details();

        return details
            ? {
                  address: details.address,
                  name: details.name,
              }
            : this.building();
    });
    protected readonly error = this.loadError;
    protected readonly isLoading = this.loading;
    protected readonly isDraft = computed(
        () => this.details()?.status === BuildingStatus.Draft
    );
    protected readonly warningUsage = computed(() => {
        const details = this.details();
        const usage = this.storageUsage();
        if (
            details?.id !== this.building().id ||
            details.status !== BuildingStatus.Published ||
            getDocumentStorageCapacityStatus(usage) === 'normal'
        ) {
            return undefined;
        }
        return usage;
    });
    protected readonly canPublish = computed(() => {
        const details = this.details();
        return details ? this.actionState.canPublish(details) : false;
    });
    protected readonly canDelete = computed(() => {
        const details = this.details();
        return details ? this.actionState.canDelete(details) : false;
    });
    //#endregion

    protected readonly compactBin = signal(false);
    private readonly subtitle = viewChild<ElementRef<HTMLElement>>('subtitle');
    private readonly binMeasure =
        viewChild<ElementRef<HTMLElement>>('binMeasure');
    private readonly binFact = viewChild('binFact', { read: ElementRef });

    constructor() {
        afterRenderEffect((onCleanup) => {
            this.details();
            const row = this.subtitle()?.nativeElement;
            const measure = this.binMeasure()?.nativeElement;
            const bin = this.binFact()?.nativeElement as
                | HTMLElement
                | undefined;
            if (!row || !measure || !bin) return;
            let frame: number | undefined;
            const update = () => {
                if (!row.clientWidth) return;
                const gap =
                    Number.parseFloat(getComputedStyle(row).columnGap) || 0;
                const siblings = Array.from(row.children).filter(
                    (child) => child !== bin && child !== measure
                );
                const otherWidth = siblings.reduce(
                    (width, child) =>
                        width +
                        Math.max(
                            child.scrollWidth,
                            child.getBoundingClientRect().width
                        ),
                    0
                );
                const sourceWidth =
                    this.details()?.buildingIdentificationNumbers ===
                    'Not yet available'
                        ? 0
                        : 24;
                const required =
                    otherWidth +
                    gap * siblings.length +
                    measure.getBoundingClientRect().width +
                    24 +
                    sourceWidth;
                this.compactBin.set(required > row.clientWidth);
            };
            const schedule = () => {
                if (frame !== undefined) return;
                frame = requestAnimationFrame(() => {
                    frame = undefined;
                    update();
                });
            };
            const observer =
                typeof ResizeObserver === 'undefined'
                    ? undefined
                    : new ResizeObserver(schedule);
            observer?.observe(row);
            observer?.observe(measure);
            for (const child of Array.from(row.children))
                observer?.observe(child);
            schedule();
            onCleanup(() => {
                observer?.disconnect();
                if (frame !== undefined) cancelAnimationFrame(frame);
            });
        });
    }

    //#region Template methods
    protected retry(): void {
        this.retryRequested.emit();
    }

    protected publishBuilding(): void {
        const details = this.details();
        if (!details) return;

        this.actionState.publish(details);
    }

    protected deleteBuilding(): void {
        const details = this.details();
        if (!details) return;

        const notifyOwner = this.onDeleted();
        this.actionState.delete(details, () => {
            notifyOwner?.(details.id);
            if (!this.destroyRef.destroyed) {
                this.deleted.emit(details.id);
            }
        });
    }
    //#endregion
}
